/** Setlist controls of the song page: "‹ Fyrra · 3 / 12 · Næsta ›" and the "Næsta lag ›" chip. */
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cx } from '../components/ui';
import { useT } from '../i18n';
import type { SetlistNavigation } from './useSetlist';

/**
 * Prev / position / next. `wide` (own row below lg: phones, iPad portrait) always shows "Fyrra" /
 * "Næsta"; in the main top-bar row (lg+, e.g. iPad landscape, where the bar is full) they appear
 * only on very wide screens (chevrons + "3 / 12" otherwise; the buttons keep aria-labels). With
 * `onShowList`, "3 / 12" is a button that opens the list's songs (the list drawer).
 */
export function SetlistNav({
  nav,
  wide = false,
  className,
  onShowList,
}: {
  nav: SetlistNavigation;
  wide?: boolean;
  className?: string;
  onShowList?: () => void;
}) {
  const t = useT();
  const label = wide ? 'inline' : 'hidden 2xl:inline';
  const btn =
    'inline-flex min-h-11 min-w-11 items-center justify-center gap-0.5 font-semibold text-[var(--accent)] transition hover:bg-[var(--accent-soft)] active:scale-95 disabled:pointer-events-none disabled:opacity-35';
  return (
    <nav
      aria-label={t('viewer.setlist_label', { name: nav.name })}
      className={cx('flex shrink-0 items-center rounded-xl ring-1 ring-[var(--line)]', wide && 'w-full justify-between', className)}
    >
      <button
        type="button"
        disabled={!nav.prev}
        onClick={() => nav.prev && nav.go(nav.prev)}
        aria-label={t('viewer.setlist_prev_song')}
        title={t('viewer.setlist_prev_song')}
        className={cx(btn, 'rounded-l-xl', wide ? 'pr-3' : 'pr-1 2xl:pr-3')}
      >
        <ChevronLeft className="size-6" aria-hidden />
        <span className={label}>{t('viewer.setlist_prev')}</span>
      </button>
      {onShowList ? (
        <button
          type="button"
          onClick={onShowList}
          aria-haspopup="dialog"
          title={t('tabs.show_list')}
          className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg px-1.5 text-[15px] font-semibold whitespace-nowrap text-[var(--ink-2)] tabular-nums underline decoration-[var(--line)] decoration-2 underline-offset-4 transition hover:bg-[var(--accent-soft)] active:scale-95"
        >
          <span aria-hidden>{t('viewer.setlist_position', { position: nav.position, total: nav.total })}</span>
          <span className="sr-only">
            {t('viewer.setlist_position_aria', { position: nav.position, total: nav.total })} – {t('tabs.show_list')}
          </span>
        </button>
      ) : (
        <span className="px-1 text-[15px] font-semibold whitespace-nowrap text-[var(--ink-2)] tabular-nums">
          <span aria-hidden>{t('viewer.setlist_position', { position: nav.position, total: nav.total })}</span>
          <span className="sr-only">{t('viewer.setlist_position_aria', { position: nav.position, total: nav.total })}</span>
        </span>
      )}
      <button
        type="button"
        disabled={!nav.next}
        onClick={() => nav.next && nav.go(nav.next)}
        aria-label={t('viewer.setlist_next_song')}
        title={t('viewer.setlist_next_song')}
        className={cx(btn, 'rounded-r-xl', wide ? 'pl-3' : 'pl-1 2xl:pl-3')}
      >
        <span className={label}>{t('viewer.setlist_next')}</span>
        <ChevronRight className="size-6" aria-hidden />
      </button>
    </nav>
  );
}

/**
 * Shown over the sheet after "next" was pressed on the last page / spread. A second press of the
 * pedal / key opens the song (the song page handles that), so pedal-only singers never need a tap.
 * It sits inside the right tap zone, so the song page ignores a tap on it until it is armed
 * (viewer/setlist nextChipArmed).
 */
export function NextSongChip({ title, onOpen }: { title: string | null; onOpen: () => void }) {
  const t = useT();
  return (
    <div className="pointer-events-none absolute right-4 z-30 flex max-w-[calc(100%-2rem)] justify-end" style={{ bottom: 'calc(var(--tabs-bottom, 0px) + 1.25rem)' }}>
      <button
        type="button"
        onClick={onOpen}
        title={t('viewer.next_song_hint')}
        aria-label={title ? `${t('viewer.next_song')}: ${title}` : t('viewer.next_song')}
        className="pointer-events-auto inline-flex min-h-13 max-w-full items-center gap-2 rounded-full bg-[var(--accent)] py-2 pr-4 pl-5 text-left text-[var(--accent-ink)] shadow-[0_6px_24px_rgb(0_0_0/0.3)] active:scale-95"
        style={{ animation: 'kgr-toast-in 160ms ease-out' }}
      >
        <span className="flex min-w-0 flex-col">
          <span className="text-[18px] leading-tight font-bold">{t('viewer.next_song')}</span>
          {title ? <span className="truncate text-[14px] opacity-85">{title}</span> : null}
        </span>
        <ChevronRight className="size-6 shrink-0" aria-hidden />
      </button>
    </div>
  );
}
